import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { DailogModalComponent } from '../../../shared/elements/dailog-modal/dailog-modal.component';

@Component({
  selector: 'app-inbuilt-visual-wrapper-v2',
  templateUrl: './inbuilt-visual-wrapper-v2.component.html',
  styleUrl: './inbuilt-visual-wrapper-v2.component.css',
  standalone: false
})
export class InbuiltVisualWrapperV2Component implements OnInit, OnDestroy {
  @Input() heading = '';
  @Input() elementData: any = null;
  @Input() config: any = null;
  @Output() navigateRequest = new EventEmitter<any>();
  @ViewChild('modalEditJSON', { static: false }) modalEditJSON: any;
  @ViewChild('modalSQLQuery', { static: false }) modalSQLQuery: any;

  isJSONeditorEnabled = false;
  isJSONeditorPopupOpen = false;
  isSQLqueryPopupOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private inbuiltUtilityService: InbuiltUtilityService,
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.inbuiltUtilityService.getJsonEditorToggleState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: boolean) => {
        this.isJSONeditorEnabled = state;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasNavigationLink(): boolean {
    return !!this.getActiveNavigationTarget();
  }

  get navigationTooltip(): string {
    const activeTarget = this.getActiveNavigationTarget();
    const tooltip = activeTarget?.toolTip ?? activeTarget?.tooltip ?? activeTarget?.ToolTip;
    return tooltip == null ? '' : String(tooltip).trim();
  }

  handleNavigationClick(): void {
    if (!this.hasNavigationLink) return;

    const navigationLink = this.elementData.navigationLink;
    this.navigateRequest.emit(navigationLink);
  }

  openJSONeditor(): void {
    this.isJSONeditorPopupOpen = true;
    this.modalEditJSON?.show();
  }

  /** Deep-clones the parent JSON (passed down from inbuilt-report-v2 via config.parentJSON) and drops
   *  this element from the current page/tab's elements array. */
  private buildParentJsonWithElementRemoved(): any {
    const cloned = JSON.parse(JSON.stringify(this.config?.parentJSON?.parent_json || {}));
    const pageKey = this.config?.currentPage?.key ?? '';
    const tabKey = this.config?.currentTab?.key ?? '';
    const tab = cloned?.pages?.[pageKey]?.tabs?.[tabKey];
    if (tab && Array.isArray(tab.elements)) {
      const elementName = String(this.elementData?.name ?? '');
      const elementType = String(this.elementData?.type ?? '').toLowerCase();
      tab.elements = tab.elements.filter((el: any) =>
        !(String(el?.name ?? '') === elementName && String(el?.type ?? '').toLowerCase() === elementType)
      );
    }
    return cloned;
  }

  deleteReport(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '400px';
    dialogConfig.hasBackdrop = true;
    dialogConfig.minHeight = '200px';
    dialogConfig.height = 'auto';
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: 'Delete Confirmation!',
      subTitle: 'Are you sure you want to delete this Element',
      buttonName: 'Continue'
    };

    const dialogRef = this.dialog.open(DailogModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data !== 'cancel') {
        const reportId = this.elementData?.element_id ?? this.elementData?.id ?? null;
        const reportName = this.config?.reportNameFromQueryParams || this.heading || this.elementData?.name || 'Report';
        const payload = {
          id: reportId,
          parent_id: this.config?.parentJSON?.id ?? null,
          parent_json: JSON.stringify(this.buildParentJsonWithElementRemoved())
        };
        this.reportsService.deleteReport(payload).subscribe({
          next: (_: any) => {
            this.toastr.success(`Visual has been deleted successfully`, '', {
              timeOut: 3000,
              positionClass: 'toast-top-center'
            });
            const report = this.config?.reportNameFromQueryParams ?? '';
             const pageKey = this.config?.currentPage?.key ?? '';
             const tabKey = this.config?.currentTab?.key ?? '';
             this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_page`);
             this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_tab`);
             this.inbuiltUtilityService.triggerRefreshChildData();
          },
          error: (_: any) => {
            this.toastr.error('Error deleting report', '', {
              timeOut: 3000,
              positionClass: 'toast-top-center'
            });
          }
        });
      }
    });
  }

  hidepopup(type: string = ''): void {
    if (type === 'sql') {
      this.isSQLqueryPopupOpen = false;
      this.modalSQLQuery?.hide();
      return;
    }
    if (type === 'edit') {
      this.isJSONeditorPopupOpen = false;
      this.modalEditJSON?.hide();
    }
  }

  onJsonEditorTextChange(ev: any): void {
    const payload = {
      report: this.config?.reportNameFromQueryParams ?? '',
      Report_Type: 'inBuilt',
      Id: this.elementData?.element_id ?? null,
      element_json: ev || {},
    };
    this.reportsService.updateReportJSON(payload).subscribe({
      next: (_: any) => {
        this.toastr.success('JSON updated successfully', '', {
          timeOut: 3000,
          positionClass: 'toast-top-center'
        });
        this.modalEditJSON?.hide();
        this.isJSONeditorPopupOpen = false;
        const report = this.config?.reportNameFromQueryParams ?? '';
        const pageKey = this.config?.currentPage?.key ?? '';
        const tabKey = this.config?.currentTab?.key ?? '';
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_page`);
        this.inbuiltUtilityService.removeCachedData(`${report}_${pageKey}_${tabKey}_data_tab`);
        this.inbuiltUtilityService.triggerRefreshChildData();
      },
      error: (_: any) => {
        this.toastr.error('Error updating JSON', '', {
          timeOut: 3000,
          positionClass: 'toast-top-center'
        });
      }
    });
  }

  openSQLquery(): void {
    this.isSQLqueryPopupOpen = true;
    this.modalSQLQuery?.show();
  }

  private getActiveNavigationTarget(): any {
    const navigationLink = this.elementData?.navigationLink;
    if (!navigationLink || typeof navigationLink !== 'object') {
      return null;
    }

    // Keep fallback keys for backward compatibility with older payloads.
    const candidates = [
      navigationLink.inReportLink,
      navigationLink.pageLink,
      navigationLink.externalLink,
    ];

    return candidates.find((candidate: any) => this.isObjectWithData(candidate)) || null;
  }

  private isObjectWithData(value: any): boolean {
    return !!value && typeof value === 'object' && Object.keys(value).length > 0;
  }

}
